/**
 * CourseCard - Course display card for browsing and enrolled courses
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, PlayCircle, ChevronRight } from 'lucide-react';

const CourseCard = ({ 
  course, 
  isEnrolled = false, 
  onPurchase, 
  onViewDetails, 
  onViewEnrolled,
  pricingPlan
}) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  if (isEnrolled) {
    return (
      <Card 
        className="cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-green-500"
        onClick={() => onViewEnrolled?.(course.course_id || course.id)}
        data-testid={`enrolled-course-${course.course_id || course.id}`}
      >
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <CardTitle className="text-base sm:text-lg">{course.course_name || course.name}</CardTitle>
            <Badge variant="secondary" className="bg-green-100 text-green-700">Enrolled</Badge>
          </div>
          <CardDescription className="text-xs sm:text-sm line-clamp-2">
            {course.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <BookOpen className="w-4 h-4" />
              <span>{course.tests_count || 0} Tests</span>
            </div>
            <Button size="sm" className="text-xs sm:text-sm">
              <PlayCircle className="w-4 h-4 mr-1" />
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer"
      onClick={() => onViewDetails?.(course)}
      data-testid={`course-card-${course.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-base sm:text-lg">{course.name}</CardTitle>
          {pricingPlan && (
            <Badge variant="outline" className="text-blue-600">
              From {formatPrice(pricingPlan.price)}
            </Badge>
          )}
        </div>
        <CardDescription className="text-xs sm:text-sm line-clamp-2">
          {course.description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              {course.tests_count || 0} Tests
            </span>
          </div>
          <Button 
            size="sm" 
            className="text-xs sm:text-sm"
            onClick={(e) => {
              e.stopPropagation();
              onPurchase?.(course);
            }}
          >
            Enroll Now
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CourseCard;
